import { tierApi } from '../services/tierApi';

// Tier utility functions for converting between display names and database enum values
// Updated to use database tier settings

// Cache for tier settings from database
let tierSettingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get tier settings from database with caching
 * @returns {Array} Array of tier settings from database
 */
export const getTierSettings = async () => {
  const now = Date.now();
  
  // Return cached data if it's still valid
  if (tierSettingsCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return tierSettingsCache;
  }
  
  try {
    const response = await tierApi.getTierSettings();
    if (response.success && response.data) {
      tierSettingsCache = response.data;
      cacheTimestamp = now;
      return tierSettingsCache;
    }
  } catch (error) {
    console.error('Error fetching tier settings:', error);
    // Fall back to default if API fails
    return getDefaultTierSettings();
  }
  
  return getDefaultTierSettings();
};

/**
 * Get default tier settings as fallback
 * @returns {Array} Array of default tier settings
 */
const getDefaultTierSettings = () => [
  { tier: 'TIER_A', tierName: 'Tier A', defaultPrice: 250 },
  { tier: 'TIER_B', tierName: 'Tier B', defaultPrice: 200 },
  { tier: 'TIER_C', tierName: 'Tier C', defaultPrice: 150 },
  { tier: 'TIER_D', tierName: 'Tier D', defaultPrice: 100 }
];

/**
 * Convert database tier enum to display name from database
 * @param {string} tierEnum - Database tier enum (e.g., 'TIER_A')
 * @returns {Promise<string>} Display name from database (e.g., 'Tier A')
 */
export const getTierDisplayName = async (tierEnum) => {
  const tierSettings = await getTierSettings();
  const tierSetting = tierSettings.find(t => t.tier === tierEnum);
  return tierSetting ? tierSetting.tierName : 'Unknown';
};

/**
 * Get all available tier options for forms (with database names)
 * @returns {Promise<Array>} Array of tier objects with value and label from database
 */
export const getTierOptions = async () => {
  const tierSettings = await getTierSettings();
  return tierSettings
    .filter(t => t.isActive !== false)
    .map(t => ({
      value: t.tier,
      label: t.tierName,
      price: t.defaultPrice
    }));
};

/**
 * Synchronous version for backward compatibility - uses cached data only
 * @param {string} tierEnum - Database tier enum (e.g., 'TIER_A')
 * @returns {string} Display name (cached or default)
 */
export const getTierDisplayNameSync = (tierEnum) => {
  if (tierSettingsCache) {
    const tierSetting = tierSettingsCache.find(t => t.tier === tierEnum);
    if (tierSetting) return tierSetting.tierName;
  }
  
  // Fallback to default mapping
  const defaultMapping = {
    'TIER_A': 'Tier A',
    'TIER_B': 'Tier B', 
    'TIER_C': 'Tier C',
    'TIER_D': 'Tier D'
  };
  return defaultMapping[tierEnum] || 'Unknown';
};

/**
 * Get tier options synchronously (uses cached data only)
 * @returns {Array} Array of tier objects with cached data
 */
export const getTierOptionsSync = () => {
  if (tierSettingsCache) {
    return tierSettingsCache
      .filter(t => t.isActive !== false)
      .map(t => ({
        value: t.tier,
        label: t.tierName,
        price: t.defaultPrice
      }));
  }
  
  // Fallback to defaults
  return getDefaultTierSettings().map(t => ({
    value: t.tier,
    label: t.tierName,
    price: t.defaultPrice
  }));
};

/**
 * Get simple tier label (A, B, C, D) from tier enum
 * @param {string} tierEnum - Database tier enum (e.g., 'TIER_A')
 * @returns {string} Simple tier label (e.g., 'A')
 */
export const getSimpleTierLabel = (tierEnum) => {
  const mapping = {
    'TIER_A': 'A',
    'TIER_B': 'B',
    'TIER_C': 'C',
    'TIER_D': 'D'
  };
  return mapping[tierEnum] || 'D';
};

/**
 * Get tournament tier options with database names
 * @returns {Promise<Array>} Array of tier objects with value and database label
 */
export const getTournamentTierOptions = async () => {
  const tierSettings = await getTierSettings();
  return tierSettings
    .filter(t => t.isActive !== false)
    .map(t => ({
      value: t.tier,
      label: t.tierName
    }));
};

/**
 * Synchronous version for backward compatibility
 * @returns {Array} Array of tier objects with cached data
 */
export const getTournamentTierOptionsSync = () => {
  if (tierSettingsCache) {
    return tierSettingsCache
      .filter(t => t.isActive !== false)
      .map(t => ({
        value: t.tier,
        label: t.tierName
      }));
  }
  
  // Fallback to defaults
  return getDefaultTierSettings().map(t => ({
    value: t.tier,
    label: t.tierName
  }));
};

/**
 * Check if a tier value is valid
 * @param {string} tier - Tier value to validate
 * @returns {boolean} True if valid
 */
export const isValidTier = (tier) => {
  const validTiers = ['TIER_A', 'TIER_B', 'TIER_C', 'TIER_D'];
  return validTiers.includes(tier);
};

